import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { FeaturePermissionName } from "@common/ADAPT.Common.Model/embed/feature-permission-name.enum";
import { CareerValuation } from "@common/ADAPT.Common.Model/organisation/career-valuation";
import { CulturalActivity } from "@common/ADAPT.Common.Model/organisation/cultural-activity";
import { CulturalActivityType } from "@common/ADAPT.Common.Model/organisation/cultural-activity-type";
import { CulturalLeadership } from "@common/ADAPT.Common.Model/organisation/cultural-leadership";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { DxGridWrapperHelper } from "@common/ux/base.component/dx-component-wrapper-builder";
import { DateFormats } from "@common/ux/date-formats";
import { IDxGridColumnCellInfo, IDxGridHeaderItem } from "@common/ux/dx.types";
import { ActivitiesService } from "@org-common/lib/activities/activities.service";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { OrgCommonCareerValuationService } from "@org-common/lib/career-valuation/org-common-career-valuation.service";
import { ViewCareerValuationDialogComponent } from "@org-common/lib/career-valuation/view-cvt-dialog/view-cvt-dialog.component";
import { CulturalActivityService } from "@org-common/lib/cultural-activity/cultural-activity.service";
import { ActivityType, CulturalActivityMetadataByType } from "@org-common/lib/cultural-activity/cultural-activity-type-metadata";
import { CulturalLeadershipFrameworkAuthService } from "@org-common/lib/cultural-leadership/cultural-leadership-framework-auth.service";
import { SupportingMemberTooltip } from "@org-common/lib/cultural-leadership/org-common-cultural-leadership.service";
import { OrganisationService } from "@org-common/lib/organisation/organisation.service";
import { CareerValuationAuthService } from "app/features/people/career-valuation/career-valuation-auth.service";
import { CareerValuationUiService } from "app/features/people/career-valuation/career-valuation-ui.service";
import { CulturalDataUtilities } from "app/features/people/cultural-data-utilities";
import { CulturalIndexService } from "app/features/people/cultural-index/cultural-index.service";
import { CulturalIndexAuthService } from "app/features/people/cultural-index/cultural-index-auth.service";
import { CulturalIndexUiService } from "app/features/people/cultural-index/cultural-index-ui.service";
import dxDataGrid, { CellPreparedEvent, InitializedEvent } from "devextreme/ui/data_grid";
import { DxTooltipComponent } from "devextreme-angular";
import { filter } from "rxjs/operators";
import { CulturalLeadershipFrameworkService } from "../cultural-leadership-framework.service";
import { CulturalLeadershipFrameworkUiService } from "../cultural-leadership-framework-ui.service";
import { ICulturalLeadershipPersonalAnalysisData } from "../cultural-leadership-personal-analysis-data.interface";

interface ICulturalLeaderMap {
    [key: number]: string;
}

@Component({
    selector: "adapt-clf-grid",
    templateUrl: "./clf-grid.component.html",
    styleUrl: "./clf-grid.component.css",
    standalone: false,
})
export class CulturalLeadershipFrameworkGridComponent extends BaseComponent implements OnInit {
    public readonly SupportingMemberTooltip = SupportingMemberTooltip;
    public readonly CulturalActivityType = CulturalActivityType;

    @ViewChild(DxTooltipComponent) public careerValuationCellTooltip?: DxTooltipComponent;
    public dateFormat = DateFormats.globalize.short;
    public dxGridWrapperHelper: DxGridWrapperHelper;

    public culturalLeadersHeaderItems?: IDxGridHeaderItem[];

    public cvtAccess?: boolean;
    public ciAccess?: boolean;

    public culturalLeadershipConfiguration?: CulturalLeadership;
    private culturalLeaderNameMap: ICulturalLeaderMap = {};

    public gridData: ICulturalLeadershipPersonalAnalysisData[] = [];
    private gridInstance?: dxDataGrid;

    public allowCulturalIndexNotes?: boolean;
    public allowCareerValuationNotes?: boolean;

    public culturalLeadershipHeaderOptions = this.culturalLeadershipUiService.getStatusHeaderOptions();
    public culturalIndexHeaderOptions = this.culturalIndexUiService.getValueHeaderOptions();
    public careerValuationHeaderOptions = this.careerValuationUiService.getValueHeaderOptions();

    public CulturalLeadershipFrameworkAuthService = CulturalLeadershipFrameworkAuthService;

    constructor(
        elementRef: ElementRef,
        public culturalIndexService: CulturalIndexService,
        public culturalIndexUiService: CulturalIndexUiService,
        private culturalLeadershipFrameworkService: CulturalLeadershipFrameworkService,
        public culturalLeadershipUiService: CulturalLeadershipFrameworkUiService,
        private authorisationService: AuthorisationService,
        public careerValuationService: OrgCommonCareerValuationService,
        public careerValuationUiService: CareerValuationUiService,
        private orgService: OrganisationService,
        private dialogService: AdaptCommonDialogService,
        private culturalActivityService: CulturalActivityService,
        activitiesService: ActivitiesService,
    ) {
        super();
        this.dxGridWrapperHelper = new DxGridWrapperHelper("adapt-clf-grid", jQuery(elementRef.nativeElement));

        this.dxGridWrapperHelper.saveGridState(`${this.orgService.getOrganisationId()}`);

        // update grid when CI/CV activities update
        activitiesService.shouldUpdate$.pipe(
            filter((types) => ArrayUtilities.intersectArrays(types, [CulturalActivityType.CareerValuation, CulturalActivityType.CulturalIndex]).length > 0),
            this.takeUntilDestroyed(),
        ).subscribe(() => this.refreshData());
    }

    public async ngOnInit() {
        await this.promiseToGetData();
        this.culturalLeadershipConfiguration = await this.culturalLeadershipFrameworkService.promiseToGetCulturalLeadershipConfiguration();

        // user may not have global quantitative read for CV/CI, but as a cultural leader
        // they will be able to see everything for people in their cohort (supporting or not)
        const canReadCvtForAnyPerson = this.gridData.some((p) => p.currentPersonHasCareerValuationAccess);
        const canReadCiForAnyPerson = this.gridData.some((p) => p.currentPersonHasCulturalIndexAccess);

        this.ciAccess = await this.authorisationService.promiseToGetHasAccess(CulturalIndexAuthService.ReadQuantitativeCulturalIndex) || canReadCiForAnyPerson;
        this.cvtAccess = await this.authorisationService.promiseToGetHasAccess(CareerValuationAuthService.ReadQuantitativeCareerValuation) || canReadCvtForAnyPerson;

        this.allowCulturalIndexNotes = this.authorisationService.currentPersonHasPermission(FeaturePermissionName.CulturalNetworkCulturalIndexRead) || canReadCiForAnyPerson;
        this.allowCareerValuationNotes = this.authorisationService.currentPersonHasPermission(FeaturePermissionName.CulturalNetworkCareerValuationRead) || canReadCvtForAnyPerson;

        // only mark columns ready once everything is loaded so that state can restore correctly
        this.dxGridWrapperHelper.callGrid((grid) => grid.setColumnsReady());
    }

    @Autobind
    public onGridInitialized(e: InitializedEvent) {
        this.dxGridWrapperHelper.initialiseGrid(e);
        this.gridInstance = e.component;

        e.component!.option().onToolbarPreparing = this.dxGridWrapperHelper.addResetToolbarButton;
    }

    @Autobind
    public editCohort(culturalLeaderId: number) {
        this.culturalLeadershipUiService.editCohortForCulturalLeaderId(culturalLeaderId).subscribe(() => this.refreshData(true));
    }

    @Autobind
    public addCohort() {
        this.culturalLeadershipUiService.addCohort().subscribe(() => this.refreshData(true));
    }

    @Autobind
    public excludePeople() {
        this.culturalLeadershipUiService.excludePeople().subscribe(() => this.refreshData(true));
    }

    @Autobind
    private async refreshData(forceRemote?: boolean) {
        await this.promiseToGetData(forceRemote);

        this.gridInstance?.refresh();
    }

    public async promiseToGetData(forceRemote?: boolean) {
        this.gridData = await this.culturalLeadershipFrameworkService.promiseToGetLatestCulturalFrameworkData(forceRemote);
        this.culturalLeadersHeaderItems = CulturalDataUtilities.getCulturalLeaderGridHeaderItems(this.gridData);
    }

    @Autobind
    public calculateCulturalLeadersCellValue(data: ICulturalLeadershipPersonalAnalysisData) {
        return data.culturalLeader?.fullName;
    }

    @Autobind
    public calculateCulturalLeadersGroupValue(data: ICulturalLeadershipPersonalAnalysisData) {
        if (data.culturalLeader) {
            this.culturalLeaderNameMap[data.culturalLeader.personId] = data.culturalLeader.fullName;

            return data.culturalLeader.personId;
        }

        return undefined;
    }

    @Autobind
    public culturalLeadersCustomizeText(e: IDxGridColumnCellInfo) {
        if (!e.valueText) {
            return "None allocated";
        }

        return this.culturalLeaderNameMap[+e.valueText] || e.valueText;
    }

    @Autobind
    public onCellPrepared(event: CellPreparedEvent) {
        if (event.rowType === "data" && event.column?.dataField === "careerValuation.extensions.actualTotal") {
            (event.cellElement as unknown as JQuery).addClass(this.careerValuationUiService.getValueCellTemplateForClass(event)!);
        }

        if (event.rowType === "data" && event.column?.dataField === "culturalIndex.value") {
            (event.cellElement as unknown as JQuery).addClass(this.culturalIndexUiService.getValueCellTemplateForClass(event)!);
        }
    }

    public launchPersonCareerValuation(cv: CareerValuation) {
        this.dialogService.open(ViewCareerValuationDialogComponent, cv).subscribe(() => {
            // refresh if CVT was deleted
            if (cv.entityAspect.entityState.isDeleted() || cv.entityAspect.entityState.isDetached()) {
                // since we get only the latest CVTs on load, the next CVT for the person will not be in the cache
                this.careerValuationService.promiseToGetLatestCareerValuationForPerson(cv.personId)
                    .then(() => this.refreshData());
            }
        });
    }

    public getPrimaryType(data: ICulturalLeadershipPersonalAnalysisData) {
        if (data.nextCVTActivity && data.nextCIActivity) {
            // both activities scheduled, primary is the soonest
            return data.nextCatchupType;
        } else if (data.nextCVTActivity) {
            // prefer CVT if scheduled activity (even if suggested CI is sooner)
            return CulturalActivityType.CareerValuation;
        } else if (data.nextCIActivity) {
            // prefer CI if scheduled activity (even if suggested CVT is sooner)
            return CulturalActivityType.CulturalIndex;
        } else {
            // neither activity is scheduled so there is no primary type
            // show them both as primary
            return undefined;
        }
    }

    public getTypeName(type?: CulturalActivityType.CareerValuation | CulturalActivityType.CulturalIndex) {
        if (type === CulturalActivityType.CareerValuation) {
            return "CV";
        }

        if (type === CulturalActivityType.CulturalIndex) {
            return "CI";
        }

        return undefined;
    }

    public getFullTypeName(type: ActivityType) {
        return CulturalActivityMetadataByType[type].shortTitle.toLocaleLowerCase();
    }

    public asCulturalActivity(event: any) {
        if (event instanceof CulturalActivity) {
            return event;
        }

        return undefined;
    }

    public asCulturalLeadershipPersonalAnalysisData(data: any) {
        if ("catchupStatus" in data) {
            return data as ICulturalLeadershipPersonalAnalysisData;
        }

        return undefined;
    }

    public rescheduleActivity(activity: CulturalActivity) {
        // no need to refresh after schedule, as shouldUpdate$ will handle that
        return this.culturalActivityService.rescheduleActivity(activity, true).subscribe();
    }

    public scheduleActivity(type: CulturalActivityType, data: ICulturalLeadershipPersonalAnalysisData) {
        return this.culturalActivityService.scheduleActivity(type, data.person, data.culturalLeader, data.nextCatchup).subscribe();
    }
}
